'use client';

import {
  BodyDefault,
  BodyStrong,
  IconButton,
  MonoSmall,
  Popover,
} from '@terra/ui-design-system';
import { Box, Divider, HStack, VStack } from '@terra/ui-styled-system';
import { InformationCircleIcon } from '@terra/ui-icons';
import { splitExampleTechnicalTokens } from './splitExampleTechnicalTokens';

type SettingsHowToUseTooltipProps = {
  ariaLabel: string;
  title: string;
  body: string;
  examplesLabel: string;
  examples: string[];
};

function ExampleWithChips({ example }: { example: string }) {
  const segments = splitExampleTechnicalTokens(example);

  return (
    <BodyDefault
      as="span"
      color="fg.tooltip.muted"
      lineHeight="1.5"
      whiteSpace="normal"
      wordBreak="break-word"
    >
      {segments.map((segment, index) => {
        if (segment.kind === 'text') {
          return <span key={`text-${index}`}>{segment.value}</span>;
        }

        return (
          <MonoSmall
            key={`chip-${index}`}
            as="span"
            display="inline"
            bg="bg.tooltipChip"
            color="fg.tooltip.default"
            borderRadius="sm"
            px="1"
            py="0.5"
            mx="0.5"
          >
            {segment.value}
          </MonoSmall>
        );
      })}
    </BodyDefault>
  );
}

export function SettingsHowToUseTooltip({
  ariaLabel,
  title,
  body,
  examplesLabel,
  examples,
}: SettingsHowToUseTooltipProps) {
  return (
    <Popover.Root
      positioning={{ strategy: 'fixed', placement: 'right', gutter: 8 }}
      closeOnInteractOutside
      closeOnEscape
      autoFocus={false}
      lazyMount
      unmountOnExit
    >
      <Popover.Trigger asChild>
        <IconButton
          type="button"
          variant="ghost"
          size="xs"
          aria-label={ariaLabel}
          color="fg.accent.muted"
          flexShrink={0}
        >
          <InformationCircleIcon size={14} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content
          bg="bg.tooltip"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="border.tooltip"
          borderRadius="l2"
          boxShadow="tooltip"
          maxW="420px"
          px="4"
          py="4.5"
          zIndex="tooltip"
        >
          <VStack alignItems="flex-start" gap="3" maxW="full">
            <BodyStrong
              color="fg.tooltip.default"
              fontSize="15"
              fontWeight="semibold"
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {title}
            </BodyStrong>
            <BodyDefault
              color="fg.tooltip.muted"
              lineHeight="1.5"
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {body}
            </BodyDefault>
            {examples.length > 0 ? (
              <>
                <Divider borderColor="border.tooltipDivider" w="full" />
                <VStack alignItems="flex-start" gap="3" w="full">
                  <MonoSmall as="span" color="fg.tooltip.subtle">
                    {examplesLabel}
                  </MonoSmall>
                  {examples.map((example) => (
                    <HStack
                      key={example}
                      alignItems="flex-start"
                      gap="2"
                      w="full"
                    >
                      <Box
                        as="span"
                        color="fg.accent.muted"
                        flexShrink={0}
                        lineHeight="1.5"
                        aria-hidden
                      >
                        •
                      </Box>
                      <ExampleWithChips example={example} />
                    </HStack>
                  ))}
                </VStack>
              </>
            ) : null}
          </VStack>
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
}
